import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Star, Users, Crown, Phone, Mail, Building2 } from 'lucide-react';
import { z } from 'zod';

const membershipSchema = z.object({
  company_name: z.string().trim().min(1, { message: "Company name is required" }).max(100, { message: "Company name must be less than 100 characters" }),
  phone: z.string().trim().min(10, { message: "Phone number must be at least 10 digits" }).max(20, { message: "Phone number must be less than 20 characters" }),
  email: z.string().trim().email({ message: "Invalid email address" }).max(255, { message: "Email must be less than 255 characters" })
});

interface MembershipDisclaimer {
  showInline?: boolean;
  className?: string;
}

const MembershipDisclaimerForm: React.FC<MembershipDisclaimer> = ({ showInline = false, className = "" }) => {
  const { language } = useLanguage();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    phone: '',
    email: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate form data
      const validatedData = membershipSchema.parse(formData);
      
      // Save to leads table with membership source
      const { error } = await supabase
        .from('leads')
        .insert({
          name: validatedData.company_name,
          phone: validatedData.phone,
          email: validatedData.email,
          subject: language === 'en' ? 'Member Pricing Interest' : 'Interés en Precios de Miembro',
          message: language === 'en' 
            ? 'Interested in becoming a member for special pricing and exclusive rates.'
            : 'Interesado en convertirse en miembro para precios especiales y tarifas exclusivas.',
          source_page: 'membership_disclaimer',
          preferred_language: language
        });

      if (error) throw error;

      // Send confirmation email
      const emailResponse = await supabase.functions.invoke('send-contact-email', {
        body: {
          type: 'membership',
          name: validatedData.company_name,
          email: validatedData.email,
          phone: validatedData.phone,
          language: language,
          company_name: validatedData.company_name
        }
      });

      if (emailResponse.error) {
        console.error('Failed to send confirmation email:', emailResponse.error);
        // Don't throw error for email failure, but log it
      }

      toast({
        title: language === 'en' ? "Application Submitted!" : "¡Solicitud Enviada!",
        description: language === 'en' 
          ? "We'll contact you soon with membership details and special pricing. Check your email for confirmation."
          : "Te contactaremos pronto con detalles de membresía y precios especiales. Revisa tu email para confirmación."
      });

      setFormData({ company_name: '', phone: '', email: '' });
      setIsOpen(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: language === 'en' ? "Validation Error" : "Error de Validación",
          description: error.errors[0].message,
          variant: "destructive"
        });
      } else {
        toast({
          title: language === 'en' ? "Error" : "Error",
          description: language === 'en' 
            ? "Failed to submit application. Please try again."
            : "Error al enviar la solicitud. Inténtalo de nuevo.",
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const disclaimerContent = (
    <Card className={`border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent ${className}`}>
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2 mb-2">
          <Crown className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg text-primary">
            {language === 'en' ? 'Get Better Rates!' : '¡Obtén Mejores Tarifas!'}
          </CardTitle>
        </div>
        <CardDescription className="text-foreground">
          {language === 'en' 
            ? 'Join our exclusive member client list and unlock special pricing on all materials and delivery services. Members save up to 15% on regular prices!'
            : '¡Únete a nuestra lista exclusiva de clientes miembros y desbloquea precios especiales en todos los materiales y servicios de entrega. Los miembros ahorran hasta 15% en precios regulares!'
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
          <div className="flex items-center gap-1">
            <Star className="h-4 w-4 text-yellow-500" />
            <span>{language === 'en' ? 'Priority Service' : 'Servicio Prioritario'}</span>
          </div>
          <div className="flex items-center gap-1">
            <Users className="h-4 w-4 text-green-600" />
            <span>{language === 'en' ? 'Volume Discounts' : 'Descuentos por Volumen'}</span>
          </div>
        </div>
        
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button className="w-full bg-primary hover:bg-primary/90">
              <Crown className="mr-2 h-4 w-4" />
              {language === 'en' ? 'Apply for Free Membership' : 'Solicitar Membresía Gratuita'}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-primary" />
                {language === 'en' ? 'Member Application' : 'Solicitud de Membresía'}
              </DialogTitle>
              <DialogDescription>
                {language === 'en'
                  ? 'Provide your company information to get started with member pricing.'
                  : 'Proporciona la información de tu empresa para comenzar con precios de miembro.'
                }
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company_name" className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  {language === 'en' ? 'Company Name *' : 'Nombre de la Empresa *'}
                </Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder={language === 'en' ? 'Your company name' : 'Nombre de tu empresa'}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  {language === 'en' ? 'Phone *' : 'Teléfono *'}
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder={language === 'en' ? 'Your phone number' : 'Tu número de teléfono'}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  {language === 'en' ? 'Email *' : 'Correo Electrónico *'}
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder={language === 'en' ? 'your@email.com' : 'tu@email.com'}
                  required
                />
              </div>
              
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading 
                  ? (language === 'en' ? 'Submitting...' : 'Enviando...')
                  : (language === 'en' ? 'Submit Application' : 'Enviar Solicitud')
                }
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );

  return showInline ? disclaimerContent : disclaimerContent;
};

export default MembershipDisclaimerForm;